<template>
    <label
        :id="id"
        :class="[
            { 'is-disabled': isDisabled },
            { 'is-bordered': border },
            { 'is-checked': isChecked },
            { 'large': size === 'large'}
        ]"
        :aria-checked="isChecked"
        :aria-disabled="isDisabled"
        :aria-label="accessibilityLabel"
        class="app-checkbox"
        role="checkbox">
        <span
            :class="{
                'is-disabled': isDisabled,
                'is-checked': isChecked,
                'is-focus': focus
            }"
            class="app-checkbox__input"
            aria-checked="mixed">
            <span class="app-checkbox__inner" />
            <input
                v-if="trueValue || falseValue"
                v-model="model"
                :name="name"
                :disabled="isDisabled"
                :true-value="trueValue"
                :false-value="falseValue"
                class="app-checkbox__original"
                type="checkbox"
                aria-hidden="true"
                @change="handleChange"
                @focus="focus = true"
                @blur="focus = false">
            <input
                v-else
                v-model="model"
                :disabled="isDisabled"
                :value="label"
                :name="name"
                class="app-checkbox__original"
                type="checkbox"
                aria-hidden="true"
                @change="handleChange"
                @focus="focus = true"
                @blur="focus = false">
        </span>
        <span v-if="$slots.default || label" class="app-checkbox__label">
            <slot />
            <template v-if="!$slots.default">{{ label }}</template>
        </span>
    </label>
</template>

<script>
export default {
    name: 'app-checkbox',
    inject: {
        AppForm: {
            default: ''
        },
        AppFormItem: {
            default: ''
        }
    },
    props: {
        value: {},
        label: {},
        disabled: Boolean,
        checked: Boolean,
        trueValue: [String, Number],
        falseValue: [String, Number],       
        name: String,
        id: String,
        border: Boolean,
        accessibilityLabel: String,
        size: String
    },
    data() {
        return {
            selfModel: false,
            focus: false,
            isLimitExceeded: false
        }
    },
    computed: {
        model: {
            get() {
                return this.value !== undefined
                    ? this.value
                    : this.selfModel
            },
            set(val) {
                this.$emit('input', val)
                this.selfModel = val
            }
        },
        isChecked() {
            if ({}.toString.call(this.model) === '[object Boolean]') {
                return this.model
            }
            else if (Array.isArray(this.model)) {
                return this.model.indexOf(this.label) > -1
            }
            else if (this.model !== null && this.model !== undefined) {
                return this.model === this.trueValue
            }
        },
        isDisabled() {
            return this.isGroup
                ? this._checkboxGroup.disabled ||
                      this.disabled ||
                      (this.elForm || {}).disabled
                : this.disabled || (this.elForm || {}).disabled
        }
    },
    methods: {
        handleChange(ev) {
            let value;
            if (ev.target.checked) {
                value = this.trueValue === undefined ? true : this.trueValue;
            }
            else {
                value = this.falseValue === undefined ? false : this.falseValue;
            }
            this.$emit('change', value, ev);
        }
    }
}
</script>

<style lang="stylus" scoped>
    @import "../../stylus/variables"
    .app-checkbox
        margin-right: 3px
        white-space: nowrap
        color: #606266
        font-weight: 500
        font-size: 14px
        cursor: pointer
        display: inline-block
        position: relative
        &.is-bordered
            padding: 9px 12px 9px 14px
            border-radius: 6px
            border: 1px solid #dcdfe6
            -webkit-box-sizing: border-box
            box-sizing: border-box
            line-height: normal
            height: 40px
            &.is-checked
                border-color: $medium-blue
            span 
                padding-left: 7px
            .app-checkbox__input
                padding-left: 0
    
    .app-checkbox__input
        padding-left: 0
        white-space: nowrap
        cursor: pointer
        outline: 0
        line-height: 1
        vertical-align: middle
        display: inline-block
        position: relative
        &.is-focus
            .app-checkbox__inner
                border-color: $medium-blue
        &.is-checked
            &:after
                -webkit-transform: rotate(45deg) scaleY(1)
                transform: rotate(45deg) scaleY(1)
            .app-checkbox__inner
                background-color: $medium-blue
                border-color: $medium-blue
                &:after
                    -webkit-transform: rotate(45deg) scaleY(1)
                    transform: rotate(45deg) scaleY(1)
    
    .app-checkbox__inner
        display: inline-block
        position: relative
        border: 1px solid #dcdfe6
        border-radius: 2px
        -webkit-box-sizing: border-box
        box-sizing: border-box
        width: 14px
        height: 14px
        background-color: #fff
        z-index: 1
        -webkit-transition: border-color .25s cubic-bezier(.71,-.46,.29,1.46),background-color .25s cubic-bezier(.71,-.46,.29,1.46)
        -o-transition: border-color .25s cubic-bezier(.71,-.46,.29,1.46),background-color .25s cubic-bezier(.71,-.46,.29,1.46)
        transition: border-color .25s cubic-bezier(.71,-.46,.29,1.46),background-color .25s cubic-bezier(.71,-.46,.29,1.46)
        &:after
            -webkit-box-sizing: content-box
            box-sizing: content-box
            content: ""
            border: 1px solid #fff
            border-left: 0
            border-top: 0
            height: 7px
            left: 4px
            position: absolute
            top: 1px
            -webkit-transform: rotate(45deg) scaleY(0)
            transform: rotate(45deg) scaleY(0)
            width: 3px
            -webkit-transition: -webkit-transform .15s ease-in .05s
            transition: -webkit-transform .15s ease-in .05s
            -o-transition: transform .15s ease-in .05s
            transition: transform .15s ease-in .05s
            transition: transform .15s ease-in .05s,-webkit-transform .15s ease-in .05s
            -webkit-transform-origin: center
            transform-origin: center
    
    .app-checkbox__original
        opacity: 0
        outline: 0
        position: absolute
        margin: 0
        width: 0
        height: 0
        z-index: -1
    .app-checkbox__label
        display: inline-block
        padding-left: 10px
        line-height: 19px
        font-size: 14px

    .large
        .app-checkbox__inner
            width: 18px
            height: 18px
            border-radius: 4px
            &:after
                height: 8px
                left: 6px
                top: 2px
                width: 3px
                border-width: 2px
    .is-disabled
        .app-checkbox__inner
            background-color: $lighter-grey
            cursor: not-allowed
</style>